"use client";

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Typography, Tooltip } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "../../components/header/Header";
import { colors } from "../../theme";
import Wrapper from "../../components/Wrapper";
import { removeCompletedTab } from "../../store/slices/product";
import Sidebar from "/src/components/Sidebar/Sidebar.tsx";
import CrispChat from "/src/components/CrispChat.jsx";
import DismissibleAuthBanner from "../../components/AuthNavBar/DismissibleAuthBanner";

/**
 * Optimize Amazon CDN image URL by appending size suffix
 * Example: image.jpg → image._SL75_.jpg
 */
const getOptimizedImageUrl = (url, size = 75) => {
  if (!url) return null;
  if (url.startsWith('blob:')) return url;
  if (url.includes('._SL')) return url;
  const match = url.match(/^(.+)(\.[^.]+)$/);
  if (match) {
    return `${match[1]}._SL${size}_${match[2]}`;
  }
  return url;
};
const getTabNameFromPathname = pathname => {
  const activeTabs = [];
  if (pathname.includes("product-listing")) activeTabs.push("Product List");
  if (pathname.includes("auditreport")) activeTabs.push("Audit Report");
  if (pathname.includes("content-enrichment")) {
    activeTabs.push("Audit Report", "Content Enrichment");
  }
  return activeTabs.length ? activeTabs : ["Product List"];
};
const getSafeAmazonBaseUrl = marketUrl => {
  const fallback = "https://amazon.de";
  try {
    const url = new URL(marketUrl || fallback);
    if (url.protocol !== "https:" && url.protocol !== "http:") return fallback;
    return `${url.protocol}//${url.host}`;
  } catch {
    return fallback;
  }
};
export default function TabHeaderLayout({
  children
}) {
  const pathname = usePathname();
  const [activeTab, setActiveTab] = useState(getTabNameFromPathname(pathname));
  const dispatch = useDispatch();
  const authUser = useSelector(state => state.auth.authUser);
  const thl = useTranslations("tabsHeaderLayout");
  useEffect(() => {
    setActiveTab(getTabNameFromPathname(pathname));
  }, [pathname]);
  const productId = useSelector(state => {
    return state?.productSlice?.productId;
  });
  const completedTabs = useSelector(state => state?.productSlice?.completedTabs);

  // Extract product ID from URL as fallback (e.g., /auditreport/123 → "123")
  const pathSegments = pathname.split('/').filter(Boolean);
  const urlProductId = pathSegments.length >= 2 ? pathSegments[1] : null;
  // Use Redux productId if available, otherwise fall back to URL
  const currentProductId = productId || urlProductId;

  // Product details for compact header
  const productName = useSelector(state => state?.productSlice?.name);
  const amazonProductId = useSelector(state => state?.productSlice?.productAmazonId);
  const productImage = useSelector(state => state?.productSlice?.imageUrl);
  const safeAmazonBaseUrl = getSafeAmazonBaseUrl(authUser?.store?.market_url);
  const isTabCompleted = tabName => completedTabs.includes(tabName);
  return <>
      <Wrapper skipAuth={false} data-sentry-element="Wrapper" data-sentry-source-file="layout.jsx">
        <Header data-sentry-element="Header" data-sentry-source-file="layout.jsx" />
        <Sidebar data-sentry-element="Sidebar" data-sentry-source-file="layout.jsx" />
        <Box width={"100%"} sx={{
        width: {
          xs: "100%",
          md: "calc(100% - 90px)"
        },
        marginTop: "72px",
        marginLeft: {
          xs: "0",
          md: "85px"
        },
        padding: "0"
      }} data-sentry-element="Box" data-sentry-source-file="layout.jsx">
          {authUser?.store?.skip_now && pathname !== "/dashboard" && <DismissibleAuthBanner text={thl("authorization")} link="/market-place" variant="warning" storageKey="authBanner_tabsHeader" />}

          {/* Compact Pill-Based Header for Audit Report & Content Enrichment */}
          {pathname.startsWith("/content-enrichment") && <Box sx={{
          width: "96%",
          mx: "auto",
          mt: 3,
          mb: 0
        }}>
              <Box sx={{
            bgcolor: colors.background.paper,
            borderRadius: "12px",
            border: `1px solid ${colors.border.light}`,
            p: {
              xs: 2,
              sm: 2.5
            }
          }}>
                {/* Row 1: Back Link + Product Info */}
                <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              mb: 2
            }}>
                  {/* Context-aware back navigation */}
                  <Link href={pathname.startsWith("/content-enrichment") ? `/auditreport/${currentProductId}` : "/product-listing"} style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "13px",
                color: colors.text.tertiary,
                textDecoration: "none",
                padding: "4px 8px",
                borderRadius: "6px",
                marginRight: "8px",
                transition: "all 0.2s ease"
              }}>
                    <ArrowBackIcon sx={{
                  fontSize: 16
                }} />
                    {pathname.startsWith("/content-enrichment") ? thl("step1Heading") : thl("backToProducts") || "Products"}
                  </Link>

                  {/* Divider */}
                  <Box sx={{
                width: "1px",
                height: 32,
                bgcolor: colors.border.light,
                display: {
                  xs: "none",
                  sm: "block"
                }
              }} />

                  {/* Product Thumbnail */}
                  <Box sx={{
                width: 44,
                height: 44,
                borderRadius: "8px",
                overflow: "hidden",
                flexShrink: 0,
                bgcolor: colors.background.light,
                border: `1px solid ${colors.border.light}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                    <Image src={getOptimizedImageUrl(productImage, 88) || "/assets/images/product/product_avatar.png"} alt="Product" width={44} height={44} style={{
                  objectFit: "contain"
                }} />
                  </Box>

                  {/* Product Name & ASIN */}
                  <Box sx={{
                flex: 1,
                minWidth: 0
              }}>
                    <Tooltip title={productName || ""} placement="top" arrow>
                      <Typography sx={{
                    fontSize: {
                      xs: "14px",
                      sm: "16px"
                    },
                    fontWeight: 600,
                    color: colors.text.primary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: {
                      xs: "180px",
                      sm: "350px",
                      md: "500px",
                      lg: "700px"
                    },
                    lineHeight: 1.3
                  }}>
                        {productName || "Loading..."}
                      </Typography>
                    </Tooltip>
                    {amazonProductId && <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.5
                }}>
                        <Typography sx={{
                    fontSize: "13px",
                    color: colors.text.tertiary
                  }}>
                          ASIN:
                        </Typography>
                        <Link href={`${safeAmazonBaseUrl}/dp/${encodeURIComponent(amazonProductId)}`} target="_blank" rel="noopener noreferrer" style={{
                    fontSize: "13px",
                    color: colors.primary.main,
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "3px"
                  }}>
                          {amazonProductId}
                          <OpenInNewIcon sx={{
                      fontSize: 14
                    }} />
                        </Link>
                      </Box>}
                  </Box>

                  {/* Step Indicators - moved to same row on larger screens */}
                  <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                ml: {
                  md: "auto"
                },
                width: {
                  xs: "100%",
                  md: "auto"
                },
                mt: {
                  xs: 1,
                  md: 0
                }
              }}>
                    {/* Step 1: Analyze */}
                    <Box component={isTabCompleted("Audit Report") ? Link : "div"} href={isTabCompleted("Audit Report") ? `/auditreport/${currentProductId}` : undefined} onClick={() => {
                  if (isTabCompleted("Audit Report")) {
                    dispatch(removeCompletedTab("Content Enrichment"));
                    setActiveTab("Audit Report");
                  }
                }} sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "18px",
                  bgcolor: pathname.startsWith("/auditreport") ? colors.primary.main : isTabCompleted("Audit Report") ? colors.decorative.green.background : colors.background.medium,
                  border: `1px solid ${pathname.startsWith("/auditreport") ? colors.primary.main : isTabCompleted("Audit Report") ? colors.decorative.green.border : colors.border.light}`,
                  cursor: isTabCompleted("Audit Report") ? "pointer" : "default",
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                  "&:hover": isTabCompleted("Audit Report") && !pathname.startsWith("/auditreport") ? {
                    bgcolor: colors.decorative.green.border
                  } : {}
                }}>
                      {/* Step number or check */}
                      <Box sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    bgcolor: pathname.startsWith("/auditreport") ? colors.background.white : isTabCompleted("Audit Report") ? colors.decorative.green.text : colors.border.medium,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                        {isTabCompleted("Audit Report") && !pathname.startsWith("/auditreport") ? <CheckIcon sx={{
                      fontSize: 14,
                      color: colors.background.white
                    }} /> : <Typography sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: pathname.startsWith("/auditreport") ? colors.primary.main : colors.text.tertiary
                    }}>
                            1
                          </Typography>}
                      </Box>
                      <Typography sx={{
                    fontSize: "13px",
                    fontWeight: pathname.startsWith("/auditreport") ? 600 : 500,
                    color: pathname.startsWith("/auditreport") ? colors.background.white : isTabCompleted("Audit Report") ? colors.decorative.green.text : colors.text.tertiary
                  }}>
                        {thl("step1Heading")}
                      </Typography>
                    </Box>

                    {/* Connector Line */}
                    <Box sx={{
                  width: 20,
                  height: 2,
                  bgcolor: isTabCompleted("Content Enrichment") || pathname.startsWith("/content-enrichment") ? colors.primary.main : colors.border.light,
                  borderRadius: 1
                }} />

                    {/* Step 2: Generate Content */}
                    <Box component={isTabCompleted("Content Enrichment") ? Link : "div"} href={isTabCompleted("Content Enrichment") ? `/content-enrichment/${currentProductId}` : undefined} sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "18px",
                  bgcolor: pathname.startsWith("/content-enrichment") ? colors.primary.main : isTabCompleted("Content Enrichment") ? colors.decorative.green.background : colors.background.medium,
                  border: `1px solid ${pathname.startsWith("/content-enrichment") ? colors.primary.main : isTabCompleted("Content Enrichment") ? colors.decorative.green.border : colors.border.light}`,
                  cursor: isTabCompleted("Content Enrichment") ? "pointer" : "default",
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                  "&:hover": isTabCompleted("Content Enrichment") && !pathname.startsWith("/content-enrichment") ? {
                    bgcolor: colors.decorative.green.border
                  } : {}
                }}>
                      {/* Step number or check */}
                      <Box sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    bgcolor: pathname.startsWith("/content-enrichment") ? colors.background.white : isTabCompleted("Content Enrichment") ? colors.decorative.green.text : colors.border.medium,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                        {isTabCompleted("Content Enrichment") && !pathname.startsWith("/content-enrichment") ? <CheckIcon sx={{
                      fontSize: 14,
                      color: colors.background.white
                    }} /> : <Typography sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: pathname.startsWith("/content-enrichment") ? colors.primary.main : colors.text.tertiary
                    }}>
                            2
                          </Typography>}
                      </Box>
                      <Typography sx={{
                    fontSize: "13px",
                    fontWeight: pathname.startsWith("/content-enrichment") ? 600 : 500,
                    color: pathname.startsWith("/content-enrichment") ? colors.background.white : isTabCompleted("Content Enrichment") ? colors.decorative.green.text : colors.text.tertiary
                  }}>
                        {thl("step2Heading")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>}
          <main>{children}</main>
        </Box>
        <CrispChat data-sentry-element="CrispChat" data-sentry-source-file="layout.jsx" />
      </Wrapper>
    </>;
}